import { useMemo, useState, useEffect, useRef, useCallback, Fragment } from "react";
import { BuchungsartFeld, buchungsartZustand } from "../components/BuchungsartFeld";
import { BelegViewer } from "../components/BelegViewer";
import { effektiveBetraege, betragJeBuchungsart } from "../../lib/rechnung.ts";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  ArrowPathIcon,
  ClockIcon,
  TrashIcon,
  CheckIcon,
  ArrowUturnLeftIcon,
  XMarkIcon,
  AdjustmentsHorizontalIcon,
  PencilSquareIcon,
  TableCellsIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { Popover, Dialog, Transition } from "@headlessui/react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  type ColumnDef,
  type VisibilityState,
  type Table as TanstackTable,
} from "@tanstack/react-table";
import {
  useReanalyze,
  useReceipt,
  useReceipts,
  useReceiptAudit,
  useUpdateReceiptHeader,
  useUpdateLineItem,
  useAddLineItem,
  useDeleteLineItem,
  useCategories,
  useTags,
  useAddTag,
  useRemoveTag,
  useDeleteReceipt,
  useBuchungsarten,
  type Buchungsart,
  type LineItemDetail,
  type LineItemField,
  type HeaderPatch,
  type UsageType,
  type Stability,
  type PaymentMethod,
} from "../lib/receipts";
import { formatCurrency, formatDate, formatDateTime, formatQuantity, PAYMENT_METHOD_LABEL, PAYMENT_METHODS_ORDER } from "../lib/format";
import { useToast } from "../components/Toast";
import { ApiError } from "../lib/api";
import { CategoryPathCombobox, type CategoryPathSelection } from "../components/CategoryPathCombobox";
import { SignedAmount } from "../components/SignedAmount";
import { devonthinkLink } from "../../lib/devonthink_link.ts";


// ── Berechnete Werte (nicht persistiert) ──────────────────────────────────
function netFromGross(gross: number, vatRate: number | null): number | null {
  if (vatRate === null || vatRate === undefined) return null;
  return gross / (1 + vatRate / 100);
}
function vatAmount(gross: number, vatRate: number | null): number | null {
  if (vatRate === null || vatRate === undefined) return null;
  return gross - gross / (1 + vatRate / 100);
}
// Pro-Position abgeleitete Werte — EINE Rechenquelle für Tabelle UND Mobile-Karten,
// damit die beiden Darstellungen nicht auseinanderdriften können.
function deriveItemValues(item: LineItemDetail, allocFn: (it: LineItemDetail) => number) {
  const alloc = allocFn(item);
  return {
    net: netFromGross(item.priceGross, item.vatRate),
    vat: vatAmount(item.priceGross, item.vatRate),
    alloc,
    eff: item.priceGross - item.productDiscount - alloc,
  };
}

export function ReceiptDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: receipt, isLoading, error } = useReceipt(id);
  const [auditOpen, setAuditOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // B3 — ←/→-Navigation durch Belege. Beim Klick aus einer Liste (Inbox/Archiv/Zuletzt/
  // Auswertungs-Drill) kommt deren geordnete ID-Liste via Router-State; so blättert man
  // durch genau die Liste, aus der man kam (kontext-folgend). Bei Direkteinstieg/Reload
  // (kein State) Fallback auf die globale Scan-Reihenfolge — nur dann wird sie geladen.
  const stateNavIds = (location.state as { navIds?: string[] } | null)?.navIds;
  const { data: fallbackList } = useReceipts({ sort: "capture" }, !stateNavIds);
  const navIds = stateNavIds ?? fallbackList?.receipts.map((r) => r.id);
  const navIdx = id && navIds ? navIds.indexOf(id) : -1; // −1 = nicht in Liste → keine Pfeile
  const prevId = navIdx > 0 ? navIds![navIdx - 1] : undefined;
  const nextId = navIdx >= 0 && navIds && navIdx < navIds.length - 1 ? navIds[navIdx + 1] : undefined;
  const navTotal = navIdx >= 0 ? navIds!.length : 0;
  const goToReceipt = (target: string | undefined) => {
    if (!target) return;
    // replace: Zurück-Button landet wieder auf der Liste statt jeden durchgeblätterten Beleg
    // erneut zu durchlaufen. navIds wird mitgereicht, damit die Navigation weiterläuft.
    navigate(`/receipts/${target}`, { state: navIds ? { navIds } : undefined, replace: true });
  };
  // Nach „Prüfung abschließen“ bzw. „Endgültig löschen“ (User 18.10.): den Beleg aus der Blätter-Liste nehmen und zum nächsten
  // springen (sonst zum vorigen); ist die Liste leer, zurück zur Liste, aus der man kam. So zählt die Inbox nur noch Ungeprüfte.
  const weiterOhneAktuellen = () => {
    const rest = (navIds ?? []).filter((x) => x !== id);
    const ziel = nextId ?? prevId;
    if (ziel) navigate(`/receipts/${ziel}`, { state: { navIds: rest }, replace: true });
    else if (location.key !== "default") navigate(-1);
    else navigate("/inbox");
  };

  if (isLoading) {
    return (
      <div className="px-5 py-8 md:px-10 md:py-10 max-w-7xl mx-auto">
        <BackLink />
        <div className="ios-card p-10 text-center text-ios-muted animate-fade-in">Lade…</div>
      </div>
    );
  }

  if (error || !receipt) {
    const is404 = error instanceof ApiError && error.status === 404;
    return (
      <div className="px-5 py-8 md:px-10 md:py-10 max-w-3xl mx-auto">
        <BackLink />
        <div className="ios-card p-10 text-center animate-fade-in">
          <ExclamationTriangleIcon className="w-12 h-12 mx-auto text-ios-orange mb-3" />
          <h2 className="text-[20px] font-semibold mb-1">
            {is404 ? "Quittung nicht gefunden" : "Fehler beim Laden"}
          </h2>
          <p className="text-[15px] text-ios-muted">
            {is404 ? "Diese Quittung existiert nicht oder wurde gelöscht." : "Bitte versuche es erneut."}
          </p>
        </div>
      </div>
    );
  }

  const fileUrl = `/api/receipts/${receipt.id}/file`;

  return (
    <div className="px-5 py-6 md:px-10 md:py-8 max-w-7xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between gap-3">
        <BackLink />
        <div className="flex items-center gap-2">
          {navTotal > 1 && (
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => goToReceipt(prevId)}
                disabled={!prevId}
                aria-label="Vorheriger Beleg"
                className="w-8 h-8 inline-flex items-center justify-center rounded-ios text-ios-blue hover:bg-ios-bg/60 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronLeftIcon className="w-5 h-5" />
              </button>
              <span className="text-[12px] text-ios-muted tabular-nums min-w-[3.2rem] text-center">{navIdx + 1} / {navTotal}</span>
              <button
                type="button"
                onClick={() => goToReceipt(nextId)}
                disabled={!nextId}
                aria-label="Naechster Beleg"
                className="w-8 h-8 inline-flex items-center justify-center rounded-ios text-ios-blue hover:bg-ios-bg/60 disabled:opacity-30 disabled:hover:bg-transparent"
              >
                <ChevronRightIcon className="w-5 h-5" />
              </button>
            </div>
          )}
          <Toolbar receiptId={receipt.id} reviewed={!!receipt.reviewedAt} hasOcrText={!!receipt.ocrRawText} items={receipt.lineItems} onShowAudit={() => setAuditOpen(true)} onWeiter={weiterOhneAktuellen} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] gap-6">
        {/* Bild/PDF Viewer — auf Mobile begrenzte Höhe (sonst füllt der iframe den
            ganzen Schirm und Kopf/Positionen darunter sind unerreichbar, weil
            Touch-Scroll im iframe nur das PDF bewegt). Sticky/Vollhöhe erst im
            zweispaltigen Desktop-Layout (lg). */}
        <section className="ios-card p-3 h-[55vh] min-h-[300px] flex flex-col overflow-hidden lg:sticky lg:top-3 lg:self-start lg:h-[calc(100vh-2rem)]">
          <BelegViewer url={fileUrl} title={`Quittung ${receipt.vendor ?? receipt.id}`} className="flex-1 min-h-0" />
        </section>

        {/* Header + Items */}
        <section className="space-y-4">
          <HeaderCard receipt={receipt} />
          <ItemsTable receiptId={receipt.id} items={receipt.lineItems} totalDiscountGross={receipt.totalDiscountGross} totalAmount={receipt.totalAmount} currency={receipt.currency} steuerjahr={receipt.steuerjahr} />
          <TagsEditor receiptId={receipt.id} tags={receipt.tags} />
          <TechMetaCard receipt={receipt} />
        </section>
      </div>

      <AuditDrawer receiptId={receipt.id} jahr={receipt.steuerjahr} open={auditOpen} onClose={() => setAuditOpen(false)} />
    </div>
  );
}

// ── Toolbar ────────────────────────────────────────────────────────────────
function Toolbar({ receiptId, reviewed, hasOcrText, items, onShowAudit, onWeiter }: { receiptId: string; reviewed: boolean; hasOcrText: boolean; items: LineItemDetail[]; onShowAudit: () => void; onWeiter: () => void }) {
  // Weg B: sichtbar machen, was „Prüfung abschließen“ mitbestätigt bzw. was noch fehlt.
  const anzahlVorschlag = items.filter((i) => buchungsartZustand(i) === "vorschlag" || (buchungsartZustand(i) === "aufgeteilt" && !i.steuerArtBestaetigt)).length;
  const anzahlLeer = items.filter((i) => buchungsartZustand(i) === "leer").length;
  const navigate = useNavigate();
  const reanalyze = useReanalyze(receiptId);
  const header = useUpdateReceiptHeader(receiptId);
  const del = useDeleteReceipt();
  const [confirmReanalyze, setConfirmReanalyze] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Migration 0014 (2026-06-05 spät): globaler Toast statt lokaler Flash-State / roter Pflicht-Box.
  // success/warn fade automatisch, error bleibt sichtbar bis Klick (User-Anforderung: kein Pop-up).
  const toast = useToast();

  return (
    <div className="flex items-center gap-1.5 flex-wrap justify-end">
        {!reviewed && (
        <button
          onClick={() =>
            header.mutate(
              { markReviewed: true },
              {
                onSuccess: (r) => {
                  if (r.blockedReason) toast.warn(`Nicht erledigt: ${r.blockedReason}`);
                  else { toast.success("Als geprüft markiert"); onWeiter(); }
                },
                onError: (e: unknown) => {
                  // Pflichtfeld-Gate (Migration 0014): Backend antwortet 400 mit {error, missing}.
                  const err = e as { payload?: { error?: string; missing?: string[] } };
                  const missing = err?.payload?.missing;
                  if (Array.isArray(missing) && missing.length > 0) {
                    toast.error("Pflichtfelder fehlen — Prüfung kann nicht abgeschlossen werden.", { missing });
                  } else {
                    toast.error(err?.payload?.error ?? "Fehler beim Speichern");
                  }
                },
              },
            )
          }
          disabled={header.isPending}
          className="text-[13px] px-3 py-1.5 rounded-ios bg-ios-blue text-white hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1"
        >
          <CheckIcon className="w-4 h-4" /> Prüfung abschließen
        </button>
      )}
      {!reviewed && (anzahlVorschlag > 0 || anzahlLeer > 0) && (
        <span className="basis-full text-right text-[12px] text-ios-muted">
          {anzahlLeer > 0 && <span className="text-ios-blue">⚠ {anzahlLeer} ohne Buchungsart</span>}
          {anzahlLeer > 0 && anzahlVorschlag > 0 && " · "}
          {anzahlVorschlag > 0 && `${anzahlVorschlag} ${anzahlVorschlag === 1 ? "Vorschlag wird" : "Vorschläge werden"} beim Abschließen mit bestätigt`}
        </span>
      )}
      {reviewed && (
        <button
          onClick={() =>
            header.mutate(
              { markReviewed: false },
              {
                onSuccess: () => {
                  toast.success("Wieder zur Prüfung geöffnet");
                },
              },
            )
          }
          disabled={header.isPending}
          title="Diesen Bon zurück in die Prüfung (Inbox) holen"
          className="text-[13px] px-3 py-1.5 rounded-ios bg-ios-line/60 hover:bg-ios-line disabled:opacity-50 inline-flex items-center gap-1"
        >
          <ArrowUturnLeftIcon className="w-4 h-4" /> Zur Prüfung
        </button>
      )}
      <button onClick={onShowAudit} className="text-[13px] px-3 py-1.5 rounded-ios bg-ios-line/60 hover:bg-ios-line inline-flex items-center gap-1">
        <ClockIcon className="w-4 h-4" /> Verlauf
      </button>
      <button
        onClick={() => setConfirmReanalyze(true)}
        disabled={!hasOcrText || reanalyze.isPending}
        title={hasOcrText ? "Bon erneut von der KI auslesen" : "Kein OCR-Text vorhanden"}
        className="text-[13px] px-3 py-1.5 rounded-ios bg-ios-line/60 hover:bg-ios-line disabled:opacity-50 inline-flex items-center gap-1"
      >
        <ArrowPathIcon className={`w-4 h-4 ${reanalyze.isPending ? "animate-spin" : ""}`} /> Neu analysieren
      </button>
      <button onClick={() => setConfirmDelete(true)} className="text-[13px] px-3 py-1.5 rounded-ios bg-ios-red/10 text-ios-red hover:bg-ios-red/20 inline-flex items-center gap-1">
        <TrashIcon className="w-4 h-4" /> Endgültig löschen
      </button>

      <ConfirmModal
        open={confirmReanalyze}
        title="Bon neu analysieren?"
        body={
          "Die KI liest den Bon erneut aus. Deine Hand-Korrekturen (Positionen + Kopf-Daten) bleiben erhalten; frisch erkannte Positionen werden hinten angehängt." +
          (reviewed ? " Der Beleg ist bereits geprüft – er geht danach zurück in die Inbox und zählt erst nach erneuter Prüfung in den Auswertungen." : "")
        }
        confirmLabel="Neu analysieren"
        pending={reanalyze.isPending}
        onCancel={() => setConfirmReanalyze(false)}
        onConfirm={() =>
          reanalyze.mutate(undefined, {
            onSuccess: (r) => {
              setConfirmReanalyze(false);
              toast.success(
                r.zurueckInInbox
                  ? "Neu analysiert. Der Beleg liegt wieder in der Inbox und zählt erst nach erneuter Prüfung in den Auswertungen."
                  : `Neu analysiert: ${r.freshItemCount} frisch, ${r.protectedItemCount} geschützt`,
              );
            },
            onError: (err: unknown) => {
              setConfirmReanalyze(false);
              // Auch bei Netzfehlern ohne Antwort eine Meldung zeigen; der Server ändert bei KI-Fehlern nichts am Beleg.
              const e = err as { payload?: { error?: string } } | null;
              toast.error(e?.payload?.error ?? "Neu analysieren fehlgeschlagen – am Beleg wurde nichts geändert.");
            },
          })
        }
      />
      <ConfirmModal
        open={confirmDelete}
        danger
        title="Quittung endgültig löschen?"
        body="Die Quittung, die PDF-Datei und alle Positionen werden unwiderruflich gelöscht (Test-Modus). Der Bon kann danach erneut hochgeladen werden."
        confirmLabel="Endgültig löschen"
        pending={del.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() =>
          del.mutate(
            { id: receiptId, hard: true },
            {
              onSuccess: () => {
                setConfirmDelete(false);
                onWeiter();
              },
              onError: () => setConfirmDelete(false),
            },
          )
        }
      />
    </div>
  );
}

// ── Header-Karte (vollständig inline-editierbar, kein Edit-Modus) ───────────
// Jedes Feld ist eine Inline-Edit-Komponente (EditableText / EditableDate /
// EditableSelect / EditableNumberNullable), die per
// Direkt-Mutation persistiert — gleicher Pattern wie die Positionen-Tabelle.
// User-Anforderung 2026-06-05: keine „Kopf bearbeiten"-Toggle, weil Friction bei
// Bulk-Sichtung (jeden Beleg erst aufklappen wäre zuviel).
function HeaderCard({ receipt }: { receipt: ReturnType<typeof useReceipt>["data"] & object }) {
  const r = receipt!;
  const update = useUpdateReceiptHeader(r.id);
  const toast = useToast();
  // Stage 3 Fallback B (2026-06-06): User-Edit am Bon-Rabatt-Feld → Backend prüft Σ-Konsistenz.
  // Bei 422 INCONSISTENT_SUMS Toast „Bitte erst die Positionsbeträge prüfen" anzeigen.
  const commit = (patch: HeaderPatch) => update.mutate(patch, {
    onError: (err: unknown) => {
      const e = err as { payload?: { error?: string; message?: string } } | undefined;
      if (e?.payload?.error === "INCONSISTENT_SUMS") {
        toast.error(e.payload.message ?? "Bitte erst die Positionsbeträge prüfen.");
      } else {
        toast.error((e?.payload?.error as string) ?? "Fehler beim Speichern");
      }
    },
  });
  return (
    <header className="ios-card p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="text-[24px] md:text-[28px] font-semibold tracking-tight">
            <EditableText big value={r.vendor ?? ""} onCommit={(v) => commit({ vendor: v.trim() || null })} />
          </div>
          <div className="text-[14px] text-ios-muted mt-0.5 flex items-center gap-1.5 flex-wrap">
            <EditableDate value={r.receiptDate} onCommit={(iso) => commit({ receiptDate: iso })} />
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[24px] md:text-[28px] font-semibold">
            <PriceGrossFieldNullable value={r.totalAmount} currency={r.currency} onCommit={(v) => commit({ totalAmount: v })} />
          </div>
          {/* Stage 3 (2026-06-06): usageType wandert vom Sub-Datum hierher — wo bisher
              „Rabatt:" stand. Bon-Rabatt & Pos-Rabatt-Σ wandern als eigene Detail-Zeilen
              unter „Filiale" weiter unten. */}
          <div className="text-[13px] text-ios-muted mt-0.5 inline-flex items-center gap-1.5 flex-wrap justify-end">
            {/* Oberflächen-Paket: die Buchungsart ersetzt die Verwendungsart (eine Steuer-Achse, wie die Steuerdatei). */}
            <BuchungsartUebersicht receipt={r} />
          </div>
        </div>
      </div>

      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[14px] mt-4 pt-4 border-t border-ios-line/60">
        <Row label="Rechtsform">
          <EditableText value={r.vendorLegalEntity ?? ""} onCommit={(v) => commit({ vendorLegalEntity: v.trim() || null })} />
        </Row>
        <Row label="Filiale">
          <EditableText value={r.vendorBranch ?? ""} onCommit={(v) => commit({ vendorBranch: v.trim() || null })} />
        </Row>
        {devonthinkLink(r.dtUuid) && (
          <Row label="Original">
            <a href={devonthinkLink(r.dtUuid)!} className="text-ios-blue" title="Öffnet den Beleg in DEVONthink (Mac).">in DEVONthink öffnen (Mac)</a>
          </Row>
        )}
        {/* Stage 3 (2026-06-06): Bon-Rabatt + Σ Pos-Rabatte als eigene Detail-Zeilen unter
            Filiale. Bon-Rabatt editierbar; Pos-Rabatt-Σ ist Read-only (rechnet aus Items).
            Zusammen ergibt Σ Effektiv = totalAmount. */}
        <Row label="Bon-Rabatt gesamt">
          <EditableNumberNullable value={r.totalDiscountGross} currency={r.currency} onCommit={(v) => commit({ totalDiscountGross: v })} discount />
        </Row>
        <Row label="Positionsrabatte gesamt">
          {(() => {
            const sumPos = r.lineItems.reduce((s, it) => s + (it.productDiscount ?? 0), 0);
            return (
              <span className="tabular-nums text-ios-muted">
                {sumPos > 0.005 ? `−${formatCurrency(sumPos, r.currency)}` : "—"}
              </span>
            );
          })()}
        </Row>
        {/* Migration 0013 (Modell B, 2026-06-05 spät): Der Beleg-weite Kostenart-Override
            wurde entfernt. Reports lesen `line_items.stability` direkt — `receipts.stability_override`
            wird nicht mehr ausgewertet. Die Kostenart sitzt jetzt pro Position als Pille im
            unteren Karten-/Tabellen-Bereich. `receipts.stability_override` bleibt vorerst im
            Schema als Sicherheitsnetz (Cleanup mit eigener Migration später). */}
        <Row label="MwSt 7%">
          <EditableNumberNullable value={r.ocrFooterVat7} currency={r.currency} onCommit={(v) => commit({ ocrFooterVat7: v })} signed />
        </Row>
        <Row label="MwSt 19%">
          <EditableNumberNullable value={r.ocrFooterVat19} currency={r.currency} onCommit={(v) => commit({ ocrFooterVat19: v })} signed />
        </Row>
        {/* Migration 0014 (2026-06-05 spät): Zahlungsart + Bezahldatum.
            paymentDate NULL = „bezahlt am Belegdatum" (Kassenbon). Bei abweichendem
            Bezahltag (Post-Rechnung) zeigen wir den Tag inkl. Differenz-Hinweis. */}
        <Row label="Bezahlt mit">
          <PaymentMethodSelect value={r.paymentMethod ?? null} onCommit={(v) => commit({ paymentMethod: v })} />
        </Row>
        <Row label="Bezahlt am">
          <PaymentDateField receiptDate={r.receiptDate} paymentDate={r.paymentDate} onCommit={(v) => commit({ paymentDate: v })} />
        </Row>
        {/* Erfasst/Quelle/OCR-Status/OCR-Engine → eigene <TechMetaCard/> unter TagsEditor,
            damit der Beleg-Header nur die fachlichen Felder zeigt (User-Anforderung 2026-06-05). */}
      </dl>

      <div className="mt-4 space-y-2">
        <div className={`flex items-center gap-2 rounded-ios px-3 py-2 text-[13px] ${
          r.ocrConfidence === "high" ? "bg-ios-green/10 text-ios-green"
          : r.ocrConfidence === "low" ? "bg-ios-orange/10 text-ios-orange"
          : "bg-ios-bg/60 text-ios-muted"
        }`}>
          <span className="font-medium">
            Plausibilität: {r.ocrConfidence === "high" ? "hoch" : r.ocrConfidence === "medium" ? "mittel" : r.ocrConfidence === "low" ? "niedrig" : "unbekannt"}
          </span>
          {r.reviewedAt && (
            <span className="ml-auto inline-flex items-center gap-1 text-ios-green">
              <CheckIcon className="w-4 h-4" /> geprüft
            </span>
          )}
        </div>
        {(r.ocrIssues ?? []).length > 0 && (
          <ul className="text-[13px] text-ios-muted space-y-0.5 px-1">
            {(r.ocrIssues ?? []).map((it, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <ExclamationTriangleIcon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-ios-orange" />
                <span>{it.message}</span>
              </li>
            ))}
          </ul>
        )}
        {update.isError && (
          <p className="text-[13px] text-ios-red px-1">{(update.error as ApiError)?.message ?? "Fehler beim Speichern"}</p>
        )}
        {!r.reviewedAt && (
          <p className="text-[12px] text-ios-subtle px-1">Prüfe die Felder direkt im Beleg und klicke „Prüfung abschließen", um ihn ins Archiv zu übernehmen.</p>
        )}
      </div>
    </header>
  );
}

// ── Items-Tabelle (Spalten, ein/ausblendbar, Inline-Edit) ──────────────────
// Hinweis: 4-Achsen-Modell (Migration 0010) hat „Subkategorie" als eigene Spalte
// abgelöst — die neue Combobox in der "category"-Spalte zeigt den vollen Pfad
// (Cat › Subcat › SubSub) und setzt alle drei IDs in einem Schritt.
type ColKey = "position" | "descriptionText" | "category" | "buchungsart" | "massnahme" | "quantity" | "pricePerUnit" | "net" | "vat" | "priceGross" | "productDiscount" | "allocDiscount" | "effective";
// Reihenfolge der Spalten: Kategorie direkt nach „Bezeichnung" — sie klassifiziert die Position
// und ist wichtiger für die Sicht als Menge/Preis (User-Anforderung 2026-06-05).
const ALL_COLS: { key: ColKey; label: string; toggleable: boolean }[] = [
  { key: "position", label: "#", toggleable: false },
  { key: "descriptionText", label: "Bezeichnung", toggleable: true },
  { key: "category", label: "Kategorie", toggleable: true },
  { key: "buchungsart", label: "Buchungsart", toggleable: true },
  { key: "massnahme", label: "Maßnahme", toggleable: true },
  { key: "quantity", label: "Menge", toggleable: true },
  { key: "pricePerUnit", label: "€/Einheit", toggleable: true },
  { key: "net", label: "Netto", toggleable: true },
  { key: "vat", label: "MwSt", toggleable: true },
  { key: "priceGross", label: "Brutto", toggleable: true },
  { key: "productDiscount", label: "Pos.-Rabatt", toggleable: true },
  { key: "allocDiscount", label: "Bon-Rabatt", toggleable: true },
  { key: "effective", label: "Eff. Preis", toggleable: true },
];

// Bon-Rabatt (allocDiscount) ist der anteilig verteilte Quittungsrabatt — computed, nicht persistiert.
const RIGHT_ALIGNED: ColKey[] = ["quantity", "pricePerUnit", "net", "vat", "priceGross", "productDiscount", "allocDiscount", "effective"];

function ItemsTable({ receiptId, items, totalDiscountGross, totalAmount, currency, steuerjahr }: { receiptId: string; items: LineItemDetail[]; totalDiscountGross: number; totalAmount: number | null; currency: string; steuerjahr: number | null }) {
  const { data: artenDaten } = useBuchungsarten(steuerjahr);
  const arten = artenDaten?.arten ?? [];
  const jahrFehlt = steuerjahr === null || artenDaten?.fehlt === true;
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [tableOpen, setTableOpen] = useState(false);
  const { data: cats } = useCategories();
  const updateItem = useUpdateLineItem(receiptId);
  const addItem = useAddLineItem(receiptId);
  const delItem = useDeleteLineItem(receiptId);

  // Eine Commit-Closure für beide Darstellungen (Tabelle + Mobile-Karten) — kein Zwilling.
  const commit = useCallback(
    (itemId: string, field: LineItemField, value: unknown) => updateItem.mutate({ itemId, field, value }),
    [updateItem],
  );
  // Combobox-Auswahl setzt alle drei Hierarchie-Felder in einer Aktion.
  // Drei sequenzielle PATCHes (Audit-Log bleibt pro Feld separat) — React-Query
  // batcht die Invalidates, das Repaint passiert nur einmal.
  const commitPath = useCallback(
    (itemId: string, sel: CategoryPathSelection) => {
      updateItem.mutate({ itemId, field: "categoryId", value: sel.categoryId });
      updateItem.mutate({ itemId, field: "subcategoryId", value: sel.subcategoryId });
      updateItem.mutate({ itemId, field: "subSubcategoryId", value: sel.subSubcategoryId });
    },
    [updateItem],
  );

  const sorted = useMemo(() => [...items].sort((a, b) => a.position - b.position), [items]);
  // Anteiliger Quittungsrabatt pro Position — computed, NICHT persistiert (CLAUDE.md-Leitprinzip).
  // Oberflächen-Paket: dieselbe cent-genaue Verteilung wie Server/Steuer-Rechnung (src/lib/rechnung.ts).
  const effMap = useMemo(() => {
    const e = effektiveBetraege(sorted, totalDiscountGross);
    return new Map(sorted.map((it, i) => [it.id, e[i]!]));
  }, [sorted, totalDiscountGross]);
  const allocated = useMemo(() => (it: LineItemDetail) => effMap.get(it.id)?.alloc ?? 0, [effMap]);
  const effektiv = useCallback((it: LineItemDetail) => effMap.get(it.id)?.eff ?? 0, [effMap]);

  // Spalten-Summen für die Summenzeile + Konsistenz-Check gegen die Kopf-Summe.
  const totals = useMemo(() => {
    let net = 0, vat = 0, gross = 0, prodDisc = 0, alloc = 0, eff = 0, vatComplete = sorted.length > 0;
    for (const it of sorted) {
      gross += it.priceGross;
      prodDisc += it.productDiscount;
      const a = allocated(it);
      alloc += a;
      eff += it.priceGross - it.productDiscount - a;
      const n = netFromGross(it.priceGross, it.vatRate);
      const v = vatAmount(it.priceGross, it.vatRate);
      if (n === null || v === null) vatComplete = false;
      else { net += n; vat += v; }
    }
    return { net, vat, gross, prodDisc, alloc, eff, vatComplete };
  }, [sorted, allocated]);

  const columns = useMemo<ColumnDef<LineItemDetail>[]>(() => {
    const money = (n: number) => formatCurrency(n, currency);
    // „*" markiert eine unvollständige Summe (nicht alle Positionen haben einen MwSt-Satz).
    const incompleteHint = "Nicht alle Positionen haben einen MwSt-Satz — Summe unvollständig";

    const footerFor: Partial<Record<ColKey, React.ReactNode>> = {
      descriptionText: <span className="text-ios-muted font-medium">Σ {sorted.length} Pos.</span>,
      // Netto + MwSt: Sub-Werte vom Brutto, daher dieselbe Farb-Konvention.
      net: totals.vatComplete
        ? <SignedAmount value={totals.net} currency={currency} />
        : <span title={incompleteHint}><SignedAmount value={totals.net} currency={currency} />*</span>,
      vat: totals.vatComplete
        ? <SignedAmount value={totals.vat} currency={currency} />
        : <span title={incompleteHint}><SignedAmount value={totals.vat} currency={currency} />*</span>,
      // Brutto- und Effektiv-Σ mit Farbe (Σ>0 = rot/Ausgabe, Σ<0 = blau/Einnahme).
      priceGross: <SignedAmount value={totals.gross} currency={currency} />,
      // Rabatt-Felder: konsequent neutral mit −Präfix (semantisch „Abzug", kein Bilanz-Vorzeichen).
      productDiscount: totals.prodDisc > 0 ? `−${money(totals.prodDisc)}` : "—",
      allocDiscount: totals.alloc > 0.005 ? `−${money(totals.alloc)}` : "—",
      effective: <span className="font-semibold"><SignedAmount value={totals.eff} currency={currency} /></span>,
    };

    const cols: ColumnDef<LineItemDetail>[] = ALL_COLS.map((c) => ({
      id: c.key,
      header: c.label,
      enableHiding: c.toggleable,
      footer: c.key in footerFor ? () => footerFor[c.key] : undefined,
      cell: ({ row }) => {
        const item = row.original;
        return (
          <ItemCell
            col={c.key}
            item={item}
            currency={currency}
            net={netFromGross(item.priceGross, item.vatRate)}
            vat={vatAmount(item.priceGross, item.vatRate)}
            alloc={allocated(item)}
            eff={effektiv(item)}
            cats={cats}
            onCommit={commit}
            onCommitPath={commitPath}
            buchungsart={{ receiptId, arten, jahrFehlt, steuerjahr }}
          />
        );
      },
    }));
    cols.push({
      id: "actions",
      header: "",
      enableHiding: false,
      cell: ({ row }) => (
        <button onClick={() => delItem.mutate(row.original.id)} title="Position löschen" className="text-ios-muted hover:text-ios-red">
          <XMarkIcon className="w-4 h-4" />
        </button>
      ),
    });
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, cats, allocated, effektiv, totals, commit, arten, jahrFehlt, steuerjahr, receiptId]);

  const table = useReactTable({
    data: sorted,
    columns,
    state: { columnVisibility },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  });

  // Konsistenz: Summe der effektiven Positionspreise gegen die Kopf-Summe (deckt z.B. F4 Pfand-Vorzeichen auf).
  const headerDelta = totalAmount !== null ? totals.eff - totalAmount : null;
  const reconciles = headerDelta !== null && Math.abs(headerDelta) <= 0.02;

  return (
    <div className="ios-card overflow-hidden">
      <div className="px-5 py-3 border-b border-ios-line/60 flex items-center justify-between gap-2">
        <h2 className="text-[15px] font-semibold">Positionen <span className="text-ios-muted font-normal">{sorted.length}</span></h2>
        <div className="flex items-center gap-1.5">
          {/* Tabellen-Ansicht (Vollbreite-Overlay mit Bon) — nur ab Laptop/iPad-Landscape sinnvoll */}
          {sorted.length > 0 && (
            <button
              onClick={() => setTableOpen(true)}
              className="hidden lg:inline-flex text-[13px] px-2.5 py-1 rounded-ios bg-ios-line/60 hover:bg-ios-line items-center gap-1"
              title="Alle Positionen als breite Tabelle anzeigen (mit Bon daneben)"
            >
              <TableCellsIcon className="w-4 h-4" /> Als Tabelle
            </button>
          )}
          <button onClick={() => addItem.mutate({})} className="text-[13px] px-2.5 py-1 rounded-ios bg-ios-blue/10 text-ios-blue hover:bg-ios-blue/20 inline-flex items-center gap-1">
            <PlusIcon className="w-4 h-4" /> Zeile
          </button>
        </div>
      </div>

      {sorted.length === 0 ? (
        <p className="px-5 py-6 text-center text-[14px] text-ios-muted">Keine Positionen erfasst.</p>
      ) : (
        <>
          {/* Default-Ansicht überall: Karten (kein seitliches Scrollen). Dieselben Edit-Bausteine +
              commit-Closure wie die Tabelle im Overlay → keine doppelte Edit-Logik. */}
          <ItemCardList items={sorted} allocated={allocated} effektiv={effektiv} commit={commit} commitPath={commitPath} cats={cats} currency={currency} onDelete={(id) => delItem.mutate(id)} buchungsart={{ receiptId, arten, jahrFehlt, steuerjahr }} />

          {/* F20: Spalten-Summen auch unter den Karten (gleiche Werte wie die Tabellen-tfoot) → Konsistenz */}
          <CardTotals totals={totals} count={sorted.length} currency={currency} />
          <ReconciliationBar effSum={totals.eff} totalAmount={totalAmount} currency={currency} headerDelta={headerDelta} reconciles={reconciles} />
        </>
      )}

      {/* Vollbreite-Tabellen-Overlay (Bon + Tabelle nebeneinander), per „Als Tabelle" geöffnet */}
      <TableOverlay
        open={tableOpen}
        onClose={() => setTableOpen(false)}
        table={table}
        receiptId={receiptId}
        currency={currency}
        effSum={totals.eff}
        totalAmount={totalAmount}
        headerDelta={headerDelta}
        reconciles={reconciles}
        onAddRow={() => addItem.mutate({})}
      />
    </div>
  );
}

type BuchungsartKontext = { receiptId: string; arten: Buchungsart[]; jahrFehlt: boolean; steuerjahr: number | null };

function ItemCell({ col, item, currency, net, vat, alloc, eff, cats, onCommit, onCommitPath, buchungsart }: {
  buchungsart: BuchungsartKontext;
  col: ColKey;
  item: LineItemDetail;
  currency: string;
  net: number | null;
  vat: number | null;
  alloc: number;
  eff: number;
  cats: CatsData;
  onCommit: (itemId: string, field: LineItemField, value: unknown) => void;
  onCommitPath: (itemId: string, selection: CategoryPathSelection) => void;
}) {
  switch (col) {
    case "position":
      return (
        <span className="text-ios-muted tabular-nums">
          {item.position}
          {item.addedManually && <span className="block text-[10px] text-ios-blue">manuell</span>}
        </span>
      );
    case "descriptionText":
      return <EditableText value={item.descriptionText} onCommit={(v) => onCommit(item.id, "descriptionText", v)} flagged={item.manualOverride} />;
    case "quantity":
      return <span className="block text-right tabular-nums text-ios-muted">{formatQuantity(item.quantity, item.unit)}</span>;
    case "pricePerUnit":
      return <span className="block text-right tabular-nums text-ios-muted">{item.pricePerUnit === null ? "—" : formatCurrency(item.pricePerUnit, currency)}</span>;
    case "category":
      // Cap auf 14rem, damit ein langer Pfad („Versicherungen u. Finanzen › Schulden › Rate Überbrückungsdarlehen")
      // die Tabellen-Spalte nicht aufbläht und alle anderen Spalten zerquetscht (table-auto).
      // Der Input hat bereits `truncate` + `title`-Tooltip, der vollständige Pfad ist also weiterhin lesbar.
      return <CategoryPathCell item={item} cats={cats} onCommitPath={onCommitPath} className="max-w-[14rem]" />;
    case "buchungsart":
      return (
        <div className="min-w-[12rem] max-w-[18rem]">
          <BuchungsartFeld item={item} betrag={eff} currency={currency} {...buchungsart} />
        </div>
      );
    case "massnahme":
      return item.istInstandhaltung ? <MassnahmeZelle item={item} currency={currency} commit={onCommit} /> : <span className="text-ios-muted">—</span>;
    case "net":
      return (
        <span className="block text-right">
          {net === null ? <span className="tabular-nums text-ios-muted">—</span> : <SignedAmount value={net} currency={currency} isNeutral={item.isNeutral === true} />}
        </span>
      );
    case "vat":
      return (
        <span className="block text-right text-ios-muted">
          {item.vatRate === null ? "—" : <span className="tabular-nums">{item.vatRate}%</span>}
          {vat !== null && <span className="block text-[11px]"><SignedAmount value={vat} currency={currency} isNeutral={item.isNeutral === true} /></span>}
        </span>
      );
    case "priceGross":
      return <PriceGrossField value={item.priceGross} currency={currency} align="right" onCommit={(v) => onCommit(item.id, "priceGross", v)} flagged={item.manualOverride} />;
    case "productDiscount":
      return <EditableNumber value={item.productDiscount} currency={currency} align="right" onCommit={(v) => onCommit(item.id, "productDiscount", v)} discount />;
    case "allocDiscount":
      return <span className="block text-right tabular-nums text-ios-muted">{alloc > 0.005 ? `−${formatCurrency(alloc, currency)}` : "—"}</span>;
    case "effective":
      return (
        <span className="block text-right font-medium">
          <SignedAmount value={eff} currency={currency} isNeutral={item.isNeutral === true} />
        </span>
      );
    default:
      return null;
  }
}

// ── 4-Achsen-Modell: EIN Pfad-Combobox statt 2 Kaskaden-Selects ────────────────
// User tippt einen Begriff (z.B. „Müll", „Geschenke", „Strom"), bekommt alle Pfade
// die das enthalten, wählt einen aus → Cat + Subcat + SubSub werden in einem Rutsch
// per drei sequenzielle PATCHes geschrieben (Audit-Log bleibt sauber pro Feld).
type CatsData = ReturnType<typeof useCategories>["data"];

function CategoryPathCell({ item, cats, onCommitPath, className }: {
  item: LineItemDetail;
  cats: CatsData;
  onCommitPath: (itemId: string, selection: CategoryPathSelection) => void;
  className?: string;
}) {
  if (!cats) return <span className="text-ios-muted">…</span>;
  return (
    <CategoryPathCombobox
      selection={{
        categoryId: item.category?.id ?? null,
        subcategoryId: item.subcategory?.id ?? null,
        subSubcategoryId: item.subSubcategory?.id ?? null,
      }}
      categories={cats.categories}
      subcategories={cats.subcategories}
      subSubcategories={cats.subSubcategories}
      onChange={(sel) => onCommitPath(item.id, sel)}
      className={className}
    />
  );
}

// ── Mobile-Karten statt 12-Spalten-Tabelle (kein seitliches Scrollen) ────────
// Nutzt dieselben Edit-Bausteine (EditableText/EditableNumber/*Select) + dieselbe
// commit-Closure wie die Tabelle — eine Bezeichnung groß, darunter Label-Wert-Paare.
function ItemCardList({ items, allocated, effektiv, commit, commitPath, cats, currency, onDelete, buchungsart }: {
  items: LineItemDetail[];
  allocated: (it: LineItemDetail) => number;
  effektiv: (it: LineItemDetail) => number;
  buchungsart: BuchungsartKontext;
  commit: (itemId: string, field: LineItemField, value: unknown) => void;
  commitPath: (itemId: string, selection: CategoryPathSelection) => void;
  cats: CatsData;
  currency: string;
  onDelete: (itemId: string) => void;
}) {
  // Select volle Breite (kein max-w) — sonst schneidet der Browser den gewählten Wert ab ("Leben" statt "Lebensmittel").
  const cardSelect = "bg-ios-bg/60 text-[16px] px-2 py-1 rounded-ios focus:ring-1 focus:ring-ios-blue outline-none max-w-full disabled:opacity-40";
  return (
    <ul className="divide-y divide-ios-line/40">
      {items.map((item) => {
        const { net, vat, alloc, eff } = deriveItemValues(item, allocated);
        return (
          <li key={item.id} className="px-4 py-3.5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <EditableText big value={item.descriptionText} onCommit={(v) => commit(item.id, "descriptionText", v)} flagged={item.manualOverride} />
                {item.addedManually && <span className="ml-1 text-[11px] text-ios-blue">manuell</span>}
              </div>
              <button onClick={() => onDelete(item.id)} title="Position löschen" className="shrink-0 -mr-1 p-1 text-ios-muted hover:text-ios-red active:opacity-60">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Reihenfolge: Kategorie ZUERST (User-Anforderung 2026-06-05 — die Klassifizierung
                ist das, was den Beleg bewertet, nicht die Menge). Danach Menge/Preise/MwSt/Rabatt. */}
            <dl className="mt-2.5 space-y-1.5 text-[15px]">
              {/* Kategorie auf Mobile vertikal: Label oben, Combobox vollbreit darunter — sonst
                  wird der Pfad „Versicherungen u. Finanzen › Schulden › Rate Überbrückungsdarlehen"
                  auf iPhone-Portrait (375 px) auf ~150 px gequetscht und truncated. */}
              <CardBlockRow label="Kategorie"><CategoryPathCell item={item} cats={cats} onCommitPath={commitPath} className={cardSelect} /></CardBlockRow>
              <CardBlockRow label="Buchungsart"><BuchungsartFeld item={item} betrag={effektiv(item)} currency={currency} {...buchungsart} /></CardBlockRow>
              {/* Migration 0013 (Modell B): Kostenart pro Position — Snapshot, nicht Stamm-Lookup.
                  Cat-Set hat den Cat-Default initial geschrieben, User-Klick auf eine Pille setzt
                  `has_stability_override=true` und schützt den Wert vor späteren Cat-Wechseln. */}
              <CardRow label="Kostenart">
                <LineItemStabilityPill
                  item={item}
                  onCommit={(v) => commit(item.id, "stability", v)}
                />
              </CardRow>
              {item.istInstandhaltung && <MassnahmeFelder item={item} currency={currency} commit={commit} selectClass={cardSelect} />}
              <CardRow label="Menge"><span className="tabular-nums text-ios-muted">{item.quantity !== null ? formatQuantity(item.quantity, item.unit) : "—"}</span></CardRow>
              <CardRow label="€/Einheit"><span className="tabular-nums text-ios-muted">{item.pricePerUnit === null ? "—" : formatCurrency(item.pricePerUnit, currency)}</span></CardRow>
              <CardRow label="Netto / MwSt">
                <span>
                  {net === null ? (
                    <span className="tabular-nums text-ios-muted">—</span>
                  ) : (
                    <>
                      <SignedAmount value={net} currency={currency} isNeutral={item.isNeutral === true} />
                      <span className="text-ios-muted"> · {item.vatRate}%</span>
                      {vat !== null && (
                        <span className="text-ios-muted">
                          {" ("}<SignedAmount value={vat} currency={currency} isNeutral={item.isNeutral === true} />{")"}
                        </span>
                      )}
                    </>
                  )}
                </span>
              </CardRow>
              <CardRow label="Brutto"><PriceGrossField value={item.priceGross} currency={currency} align="right" onCommit={(v) => commit(item.id, "priceGross", v)} flagged={item.manualOverride} /></CardRow>
              {/* F19: Rabatt-Zeilen immer zeigen — deckungsgleich mit der Tabelle (auch bei 0,00 / „—"). */}
              <CardRow label="Pos.-Rabatt"><EditableNumber value={item.productDiscount} currency={currency} align="right" onCommit={(v) => commit(item.id, "productDiscount", v)} discount /></CardRow>
              <CardRow label="Bon-Rabatt"><span className="tabular-nums text-ios-muted">{alloc > 0.005 ? `−${formatCurrency(alloc, currency)}` : "—"}</span></CardRow>
            </dl>

            <div className="mt-2.5 pt-2.5 border-t border-ios-line/40 flex items-center justify-between">
              <span className="text-[13px] text-ios-muted">Effektiver Preis</span>
              <span className="text-[17px] font-semibold">
                <SignedAmount value={eff} currency={currency} isNeutral={item.isNeutral === true} />
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

// ── Instandhaltung je Position (24.10.2026) ─────────────────────────────────────
// Nur Angaben für die Jahresabschluss-Prüfliste (4.000-€-Frage, Herstellung, 3 von 4 Bereichen, § 35a) — die Rechnung ändert sich nicht.
const MASSNAHME_BEREICHE: { value: LineItemDetail["massnahmeBereich"]; label: string }[] = [
  { value: null, label: "—" }, { value: "heizung", label: "Heizung" }, { value: "sanitaer", label: "Sanitär" },
  { value: "elektro", label: "Elektro" }, { value: "fenster", label: "Fenster" }, { value: "sonstige", label: "sonstiges" },
];
function MassnahmeFelder({ item, currency, commit, selectClass }: {
  item: LineItemDetail; currency: string; selectClass: string;
  commit: (itemId: string, field: LineItemField, value: unknown) => void;
}) {
  const arten: { label: string; value: LineItemDetail["massnahmeArt"]; title: string }[] = [
    { label: "Erhaltung", value: "erhaltung", title: "Reparatur/Ersatz gleicher Art — sofort abziehbar" },
    { label: "Herstellung", value: "herstellung", title: "Neu, Erweiterung oder deutlich besser als vorher — nur Abschreibung (BMF 18.07.2003)" },
    { label: "—", value: null, title: "noch nicht bestimmt" },
  ];
  const v = item.massnahmeVorschlag;
  const vorschlag = (text: string, f: LineItemField, wert: unknown) => (
    <button type="button" onClick={() => commit(item.id, f, wert)} title="Vorschlag übernehmen"
      className="ml-2 text-[12px] text-ios-blue underline decoration-dotted">Vorschlag: {text} übernehmen</button>
  );
  return (
    <>
      {v && (v.art || v.bereich || v.lohnanteil !== null || v.massnahme) && (
        <p className="text-[12px] text-ios-muted">Vorschläge aus Text und Betrag — erst übernommene Werte zählen in der Prüfliste.</p>
      )}
      <CardRow label="Maßnahme-Art">
        <div className="inline-flex rounded-ios bg-ios-bg/60 p-0.5 text-[13px]" role="radiogroup" aria-label="Art der Maßnahme">
          {arten.map((o) => (
            <button key={o.label} type="button" role="radio" aria-checked={item.massnahmeArt === o.value} title={o.title}
              onClick={() => commit(item.id, "massnahmeArt", o.value)}
              className={`px-2.5 py-0.5 rounded-[7px] transition ${item.massnahmeArt === o.value ? "bg-white shadow-sm font-medium" : "text-ios-muted hover:text-ios-ink"}`}>
              {o.label}
            </button>
          ))}
        </div>
        {v?.art && vorschlag("Erhaltung (bis 4.000 € netto)", "massnahmeArt", v.art)}
      </CardRow>
      <CardSelectRow label="Bereich">
        <select className={selectClass} value={item.massnahmeBereich ?? ""} aria-label="Bereich der Maßnahme"
          title="Zentrale Ausstattungsmerkmale (BMF 18.07.2003 Rz. 10): Heizung, Sanitär, Elektro, Fenster"
          onChange={(e) => commit(item.id, "massnahmeBereich", e.target.value === "" ? null : e.target.value)}>
          {MASSNAHME_BEREICHE.map((b) => <option key={b.label} value={b.value ?? ""}>{b.label}</option>)}
        </select>
        {v?.bereich && vorschlag(MASSNAHME_BEREICHE.find((b) => b.value === v.bereich)?.label ?? v.bereich, "massnahmeBereich", v.bereich)}
      </CardSelectRow>
      <CardRow label="Maßnahme"><EditableText value={item.massnahme ?? ""} onCommit={(x) => commit(item.id, "massnahme", x)} />
        {v?.massnahme && vorschlag(`„${v.massnahme}“`, "massnahme", v.massnahme)}</CardRow>
      <CardRow label="Lohnanteil (§ 35a)">
        <EditableNumberNullable value={item.lohnanteil} currency={currency} onCommit={(x) => commit(item.id, "lohnanteil", x)} />
        {v && v.lohnanteil !== null && vorschlag(formatCurrency(v.lohnanteil, currency), "lohnanteil", v.lohnanteil)}
      </CardRow>
    </>
  );
}

// Tabellen-Ansicht: kompakte Zusammenfassung, Klick klappt dieselben Felder wie in der Karte auf.
function MassnahmeZelle({ item, currency, commit }: { item: LineItemDetail; currency: string; commit: (itemId: string, field: LineItemField, value: unknown) => void }) {
  const [offen, setOffen] = useState(false);
  const kurz = [item.massnahmeArt === "erhaltung" ? "Erhaltung" : item.massnahmeArt === "herstellung" ? "Herstellung" : null,
    MASSNAHME_BEREICHE.find((b) => b.value === item.massnahmeBereich && b.value)?.label ?? null,
    item.lohnanteil !== null ? `Lohn ${formatCurrency(item.lohnanteil, currency)}` : null].filter(Boolean).join(" · ");
  return (
    <div className="min-w-[10rem]">
      <button type="button" onClick={() => setOffen(!offen)} className="text-left text-[13px] text-ios-blue">{kurz || "angeben…"} {offen ? "▴" : "▾"}</button>
      {offen && <dl className="mt-1 space-y-1.5 text-[13px] min-w-[18rem]"><MassnahmeFelder item={item} currency={currency} commit={commit}
        selectClass="bg-ios-bg/60 px-2 py-1 rounded-ios max-w-full" /></dl>}
    </div>
  );
}

// ── Modell B: Pille pro Position für Kostenart (fix | variabel | —) ────────────
// Klick = expliziter User-Wille → Backend setzt `has_stability_override=true`,
// damit Cat-Wechsel / Re-Analyze den Wert nicht überschreiben. „—" = bewusst
// nicht klassifiziert (Position landet im „ohne Kostenart"-Bucket der Auswertungen).
function LineItemStabilityPill({
  item,
  onCommit,
}: {
  item: LineItemDetail;
  onCommit: (value: Stability | null) => void;
}) {
  const opts: { label: string; value: Stability | null }[] = [
    { label: "variabel", value: "variable" },
    { label: "fix", value: "fixed" },
    { label: "—", value: null },
  ];
  return (
    <div className="inline-flex items-center gap-1.5">
      <div
        className="inline-flex rounded-ios bg-ios-bg/60 p-0.5 text-[13px]"
        role="radiogroup"
        aria-label="Kostenart"
        title={item.hasStabilityOverride ? "Vom User explizit gesetzt — Cat-Wechsel ändert das nicht." : "Aus der Kategorie übernommen — wird beim Cat-Wechsel mitgezogen."}
      >
        {opts.map((opt) => {
          const active = item.stability === opt.value;
          return (
            <button
              key={opt.label}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onCommit(opt.value)}
              className={`px-2.5 py-0.5 rounded-[7px] transition ${
                active ? "bg-white shadow-sm font-medium" : "text-ios-muted hover:text-ios-ink"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {item.hasStabilityOverride && (
        <span
          className="w-1.5 h-1.5 rounded-full bg-ios-blue"
          title="Override aktiv — Cat-Wechsel ändert die Kostenart nicht mehr."
          aria-label="Override aktiv"
        />
      )}
    </div>
  );
}

function CardRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 min-w-0">
      <dt className="text-ios-muted shrink-0">{label}</dt>
      <dd className="text-ios-ink text-right min-w-0 truncate">{children}</dd>
    </div>
  );
}

// Wie CardRow, aber für <select>: gibt dem Select den vollen Rest der Zeile (kein truncate),
// damit lange Kategorienamen nicht abgeschnitten werden.
function CardSelectRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-ios-muted shrink-0">{label}</dt>
      <dd className="min-w-0 flex-1 text-right">{children}</dd>
    </div>
  );
}

// Vertikales Layout für Felder, deren Wert (z.B. Combobox-Pfad) auf engem Mobile-Screen
// die volle Karten-Breite braucht — Label oben, Wert darunter linksbündig.
function CardBlockRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 min-w-0">
      <dt className="text-ios-muted">{label}</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

// F20: Summen-Box für die Karten-Ansicht — spiegelt 1:1 die Spalten-Fußzeile (tfoot) der Tabelle
// aus denselben `totals`, damit beide Ansichten dieselben Summen zeigen.
type Totals = { net: number; vat: number; gross: number; prodDisc: number; alloc: number; eff: number; vatComplete: boolean };
function CardTotals({ totals, count, currency }: { totals: Totals; count: number; currency: string }) {
  const m = (n: number) => formatCurrency(n, currency);
  const incompleteHint = "Nicht alle Positionen haben einen MwSt-Satz — Summe unvollständig";
  return (
    <div className="px-4 py-3 border-t-2 border-ios-line/80 bg-ios-bg/30">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[12px] font-semibold uppercase tracking-wide text-ios-muted">Summen</span>
        <span className="text-[12px] text-ios-muted">Σ {count} Pos.</span>
      </div>
      <dl className="space-y-1 text-[14px] tabular-nums">
        <SumRow label="Netto">{totals.vatComplete
          ? <SignedAmount value={totals.net} currency={currency} />
          : <span title={incompleteHint}><SignedAmount value={totals.net} currency={currency} />*</span>}</SumRow>
        <SumRow label="MwSt">{totals.vatComplete
          ? <SignedAmount value={totals.vat} currency={currency} />
          : <span title={incompleteHint}><SignedAmount value={totals.vat} currency={currency} />*</span>}</SumRow>
        <SumRow label="Brutto"><SignedAmount value={totals.gross} currency={currency} /></SumRow>
        <SumRow label="Pos.-Rabatt">{totals.prodDisc > 0 ? `−${m(totals.prodDisc)}` : "—"}</SumRow>
        <SumRow label="Bon-Rabatt">{totals.alloc > 0.005 ? `−${m(totals.alloc)}` : "—"}</SumRow>
        <SumRow label="Effektiv"><span className="font-semibold"><SignedAmount value={totals.eff} currency={currency} /></span></SumRow>
      </dl>
    </div>
  );
}
function SumRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-ios-muted not-italic">{label}</dt>
      <dd className="text-ios-ink">{children}</dd>
    </div>
  );
}

function ColumnToggle({ table }: { table: TanstackTable<LineItemDetail> }) {
  return (
    <Popover className="relative">
      <Popover.Button className="text-[13px] px-2.5 py-1 rounded-ios bg-ios-line/60 hover:bg-ios-line inline-flex items-center gap-1">
        <AdjustmentsHorizontalIcon className="w-4 h-4" /> Spalten
      </Popover.Button>
      <Popover.Panel className="absolute right-0 z-20 mt-1 w-52 ios-card p-2 shadow-card">
        {table
          .getAllLeafColumns()
          .filter((col) => col.getCanHide())
          .map((col) => (
            <label key={col.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-ios-bg/60 cursor-pointer text-[14px]">
              <input type="checkbox" checked={col.getIsVisible()} onChange={col.getToggleVisibilityHandler()} className="accent-ios-blue" />
              {String(col.columnDef.header)}
            </label>
          ))}
      </Popover.Panel>
    </Popover>
  );
}

// ── Konsistenz-Leiste (Σ effektive Positionen vs. Kopf-Summe) — in Karten- UND Tabellen-Ansicht ──
function ReconciliationBar({ effSum, totalAmount, currency, headerDelta, reconciles }: {
  effSum: number;
  totalAmount: number | null;
  currency: string;
  headerDelta: number | null;
  reconciles: boolean;
}) {
  return (
    <div className="px-5 py-2.5 border-t border-ios-line/60 flex items-center justify-between gap-3 text-[13px]">
      <span className="text-ios-muted">
        Σ Positionen (effektiv): <span className="font-medium"><SignedAmount value={effSum} currency={currency} /></span>
        <span className="mx-2">·</span>
        Kopf-Summe: <span className="font-medium"><SignedAmount value={totalAmount} currency={currency} /></span>
      </span>
      {headerDelta === null ? (
        <span className="text-ios-muted">keine Kopf-Summe</span>
      ) : reconciles ? (
        <span className="inline-flex items-center gap-1 text-ios-blue"><CheckIcon className="w-4 h-4" /> stimmig</span>
      ) : (
        <span
          className="inline-flex items-center gap-1 text-ios-orange"
          title="Positionssumme weicht von der Kopf-Summe ab — z.B. Pfand/Leergut mit falschem Vorzeichen oder eine fehlende Position."
        >
          <ExclamationTriangleIcon className="w-4 h-4" /> Δ {headerDelta > 0 ? "+" : ""}{formatCurrency(headerDelta, currency)}
        </span>
      )}
    </div>
  );
}

// ── Vollbreite-Tabellen-Overlay (Bon links, Tabelle rechts) ──────────────────
// Rendert dieselbe TanStack-table-Instanz wie sonst nirgendwo (nur hier) → Editieren
// im Overlay läuft über dieselben Edit-Bausteine + commit-Closure, kein Zwilling.
function TableOverlay({ open, onClose, table, receiptId, currency, effSum, totalAmount, headerDelta, reconciles, onAddRow }: {
  open: boolean;
  onClose: () => void;
  table: TanstackTable<LineItemDetail>;
  receiptId: string;
  currency: string;
  effSum: number;
  totalAmount: number | null;
  headerDelta: number | null;
  reconciles: boolean;
  onAddRow: () => void;
}) {
  const [pdfOpen, setPdfOpen] = useState(true);
  const closeRef = useRef<HTMLButtonElement>(null);
  const fileUrl = `/api/receipts/${receiptId}/file`;
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} initialFocus={closeRef} className="relative z-40">
        <Transition.Child as={Fragment} enter="transition-opacity" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <Transition.Child as={Fragment} enter="transition" enterFrom="scale-95 opacity-0" enterTo="scale-100 opacity-100" leave="transition" leaveFrom="scale-100 opacity-100" leaveTo="scale-95 opacity-0">
          <Dialog.Panel className="fixed inset-x-3 inset-y-4 bg-ios-card rounded-iosXl shadow-xl flex flex-col overflow-hidden">
            {/* Kopfzeile */}
            <div className="px-4 py-3 border-b border-ios-line/60 flex items-center justify-between gap-2 shrink-0">
              <Dialog.Title className="text-[15px] font-semibold">Positionen — Tabelle</Dialog.Title>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setPdfOpen((v) => !v)}
                  className={`text-[13px] px-2.5 py-1 rounded-ios inline-flex items-center gap-1 ${pdfOpen ? "bg-ios-blue/10 text-ios-blue" : "bg-ios-line/60 hover:bg-ios-line"}`}
                  title={pdfOpen ? "Bon ausblenden (mehr Platz für die Tabelle)" : "Bon zum Vergleich einblenden"}
                >
                  <DocumentTextIcon className="w-4 h-4" /> Bon
                </button>
                <ColumnToggle table={table} />
                <button onClick={onAddRow} className="text-[13px] px-2.5 py-1 rounded-ios bg-ios-blue/10 text-ios-blue hover:bg-ios-blue/20 inline-flex items-center gap-1">
                  <PlusIcon className="w-4 h-4" /> Zeile
                </button>
                <button ref={closeRef} onClick={onClose} title="Schließen" className="ml-1 p-1 text-ios-muted hover:text-ios-ink">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* Körper: Bon | Tabelle */}
            <div className="flex-1 flex min-h-0">
              {pdfOpen && (
                <div className="w-[34%] min-w-[260px] border-r border-ios-line/60 bg-ios-bg/40 p-2 shrink-0">
                  <BelegViewer url={fileUrl} title="Bon" />
                </div>
              )}
              <div className="flex-1 overflow-auto">
                <table className="w-full text-[14px]">
                  <thead className="sticky top-0 z-10 text-[12px] uppercase tracking-wide text-ios-muted bg-ios-bg">
                    {table.getHeaderGroups().map((hg) => (
                      <tr key={hg.id}>
                        {hg.headers.map((h) => {
                          const right = RIGHT_ALIGNED.includes(h.column.id as ColKey);
                          return (
                            <th key={h.id} className={`font-medium px-3 py-2 ${h.column.id === "actions" ? "w-8" : right ? "text-right" : "text-left"}`}>
                              {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id} className="border-t border-ios-line/40 hover:bg-ios-bg/30 align-top">
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className={`px-3 py-1.5 ${cell.column.id === "actions" ? "text-right w-8" : ""}`}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="border-t-2 border-ios-line/80 bg-ios-bg/30 text-[13px] tabular-nums">
                    {table.getFooterGroups().map((fg) => (
                      <tr key={fg.id}>
                        {fg.headers.map((h) => {
                          const right = RIGHT_ALIGNED.includes(h.column.id as ColKey);
                          return (
                            <td key={h.id} className={`px-3 py-2 ${h.column.id === "actions" ? "w-8" : right ? "text-right" : "text-left"}`}>
                              {h.isPlaceholder || h.column.columnDef.footer == null ? null : flexRender(h.column.columnDef.footer, h.getContext())}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tfoot>
                </table>
              </div>
            </div>
            {/* Fußzeile: Konsistenz-Check (live beim Editieren) */}
            <div className="shrink-0">
              <ReconciliationBar effSum={effSum} totalAmount={totalAmount} currency={currency} headerDelta={headerDelta} reconciles={reconciles} />
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}

// ── Editierbare Zellen ──────────────────────────────────────────────────────
function EditableText({ value, onCommit, flagged, big }: { value: string; onCommit: (v: string) => void; flagged?: boolean; big?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} className={`text-left w-full -ml-1 px-1 py-0.5 rounded hover:bg-ios-bg/60 ${big ? "text-[16px] font-medium" : ""}`}>
        {value || <span className="text-ios-muted">—</span>}
        {flagged && <span className="ml-1 text-[10px] text-ios-blue align-top">✎</span>}
      </button>
    );
  }
  const done = () => { setEditing(false); if (draft !== value) onCommit(draft); };
  // text-[16px] auf Mobile verhindert iOS-Safari-Auto-Zoom beim Fokus; lg zurück auf 14px (kompakte Tabelle).
  return (
    <input
      ref={ref}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={done}
      onKeyDown={(e) => {
        // stopPropagation: sonst schließt Enter/Escape ein umgebendes Dialog-Overlay mit.
        if (e.key === "Enter") { e.stopPropagation(); done(); }
        if (e.key === "Escape") { e.stopPropagation(); setDraft(value); setEditing(false); }
      }}
      className={`w-full -ml-1 px-1 py-0.5 rounded bg-white ring-1 ring-ios-blue outline-none ${big ? "text-[16px] font-medium" : "text-[16px] lg:text-[14px]"}`}
    />
  );
}

function EditableNumber({ value, currency, align, onCommit, flagged, signed, discount }: { value: number; currency: string; align?: "right"; onCommit: (v: number) => void; flagged?: boolean; signed?: boolean; discount?: boolean }) {
  const [editing, setEditing] = useState(false);
  // Edit-Mode: bei discount-Feldern zeigen wir das Minus visuell als Prefix, der draft enthält
  // nur den Absolutwert. Der User tippt „0,80" und sieht „− 0,80" — keine versteckte Konvention.
  const initialDraft = () => discount ? String(Math.abs(value)) : String(value);
  const [draft, setDraft] = useState(initialDraft);
  useEffect(() => setDraft(initialDraft()), [value, discount]); // eslint-disable-line react-hooks/exhaustive-deps
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) ref.current?.select(); }, [editing]);

  if (!editing) {
    // discount: DB-Konvention positiv, Display rendert das Minus. Wir nutzen Math.abs damit auch
    // historisch versehentlich negativ gespeicherte Werte (Live-Bug 2026-06-05 vor diesem Fix)
    // korrekt als „−Betrag" angezeigt werden, nicht als „—".
    const display = discount
      ? (Math.abs(value) > 0.005 ? `−${formatCurrency(Math.abs(value), currency)}` : "—")
      : signed
      ? null
      : formatCurrency(value, currency);
    return (
      <button onClick={() => setEditing(true)} className={`w-full -ml-1 px-1 py-0.5 rounded hover:bg-ios-bg/60 tabular-nums ${align === "right" ? "text-right" : "text-left"}`}>
        {signed && !discount ? <SignedAmount value={value} currency={currency} /> : display}
        {flagged && <span className="ml-1 text-[10px] text-ios-blue align-top">✎</span>}
      </button>
    );
  }
  const done = () => {
    setEditing(false);
    // Auto-ABS bei discount-Feldern: User darf „-0,80" oder „0,80" tippen, beide landen positiv
    // in der DB. Robuste Konvention statt versteckter Regel.
    const raw = Number(draft.replace(",", "."));
    const n = discount ? Math.abs(raw) : raw;
    if (Number.isFinite(n) && n !== value) onCommit(n);
    else setDraft(initialDraft());
  };
  const input = (
    <input
      ref={ref}
      inputMode="decimal"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={done}
      onKeyDown={(e) => {
        // stopPropagation: sonst schließt Enter/Escape ein umgebendes Dialog-Overlay mit.
        if (e.key === "Enter") { e.stopPropagation(); done(); }
        if (e.key === "Escape") { e.stopPropagation(); setDraft(initialDraft()); setEditing(false); }
      }}
      className="w-20 px-1 py-0.5 rounded bg-white ring-1 ring-ios-blue outline-none text-[16px] lg:text-[14px] text-right tabular-nums"
    />
  );
  if (discount) {
    return (
      <span className="inline-flex items-center gap-0.5">
        <span className="text-ios-muted text-[14px] select-none" aria-hidden>−</span>
        {input}
      </span>
    );
  }
  return input;
}

// Position-Brutto-Field: kleiner +/− Toggle vor dem EditableNumber-View. Klick auf den Toggle
// invertiert das Vorzeichen (Einnahme ↔ Ausgabe), ohne dass der User „-25" tippen muss.
// User-Beschluss 2026-06-05 (Pattern 1 aus 3 Optionen): +/− Mini-Toggle pro Betrag.
function PriceGrossField({ value, currency, onCommit, flagged, align }: {
  value: number;
  currency: string;
  onCommit: (v: number) => void;
  flagged?: boolean;
  align?: "right";
}) {
  const isIncome = value < 0;
  const isExpense = value > 0;
  return (
    <div className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end w-full" : ""}`}>
      <button
        type="button"
        onClick={() => { if (value !== 0) onCommit(-value); }}
        title={isIncome ? "Auf Ausgabe umschalten" : isExpense ? "Auf Einnahme umschalten" : "Einen Wert eingeben, dann lässt sich das Vorzeichen umschalten."}
        disabled={value === 0}
        aria-label="Einnahme/Ausgabe umschalten"
        className={`text-[11px] px-1 rounded transition ${isIncome ? "text-ios-blue font-semibold" : isExpense ? "text-ios-red font-semibold" : "text-ios-muted"} disabled:opacity-40 hover:bg-ios-bg/60`}
      >
        +/−
      </button>
      <EditableNumber value={value} currency={currency} align={align} onCommit={onCommit} flagged={flagged} signed />
    </div>
  );
}

// EditableNumber für nullable Felder (MwSt-Footer kann fehlen).
// "—" als Read-State, leerer Input als Commit-„auf null setzen"-Pfad.
function EditableNumberNullable({ value, currency, onCommit, signed, discount }: { value: number | null; currency: string; onCommit: (v: number | null) => void; signed?: boolean; discount?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  useEffect(() => setDraft(value === null ? "" : String(value)), [value]);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) ref.current?.select(); }, [editing]);

  if (!editing) {
    let content: React.ReactNode;
    if (value === null) content = <span className="text-ios-muted">—</span>;
    // Auto-ABS im Display (tolerant gegen historisch versehentlich negativ gespeicherte Werte).
    else if (discount) content = Math.abs(value) > 0.005 ? `−${formatCurrency(Math.abs(value), currency)}` : <span className="text-ios-muted">—</span>;
    else if (signed) content = <SignedAmount value={value} currency={currency} />;
    else content = formatCurrency(value, currency);
    return (
      <button onClick={() => setEditing(true)} className="-ml-1 px-1 py-0.5 rounded hover:bg-ios-bg/60 tabular-nums">
        {content}
      </button>
    );
  }
  const done = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed === "") { if (value !== null) onCommit(null); return; }
    const raw = Number(trimmed.replace(",", "."));
    // Auto-ABS bei discount-Feldern (siehe EditableNumber-Variante).
    const n = discount ? Math.abs(raw) : raw;
    if (Number.isFinite(n) && n !== value) onCommit(n);
    else setDraft(value === null ? "" : String(discount ? Math.abs(value ?? 0) : value));
  };
  const input = (
    <input
      ref={ref}
      inputMode="decimal"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={done}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.stopPropagation(); done(); }
        if (e.key === "Escape") { e.stopPropagation(); setDraft(value === null ? "" : String(discount ? Math.abs(value) : value)); setEditing(false); }
      }}
      className="w-24 -ml-1 px-1 py-0.5 rounded bg-white ring-1 ring-ios-blue outline-none text-[16px] lg:text-[14px] text-left tabular-nums"
    />
  );
  if (discount) {
    return (
      <span className="inline-flex items-center gap-0.5">
        <span className="text-ios-muted text-[14px] select-none" aria-hidden>−</span>
        {input}
      </span>
    );
  }
  return input;
}

// Nullable Variante von PriceGrossField (für Kopf-Summe totalAmount). +/− Toggle nur
// aktiv wenn ein numerischer Wert da ist (null = nichts zu invertieren).
function PriceGrossFieldNullable({ value, currency, onCommit }: {
  value: number | null;
  currency: string;
  onCommit: (v: number | null) => void;
}) {
  const isIncome = value !== null && value < 0;
  const isExpense = value !== null && value > 0;
  return (
    <div className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={() => { if (value !== null && value !== 0) onCommit(-value); }}
        title={isIncome ? "Auf Ausgabe umschalten" : isExpense ? "Auf Einnahme umschalten" : "Einen Wert eingeben, dann lässt sich das Vorzeichen umschalten."}
        disabled={value === null || value === 0}
        aria-label="Einnahme/Ausgabe umschalten"
        className={`text-[11px] px-1 rounded transition ${isIncome ? "text-ios-blue font-semibold" : isExpense ? "text-ios-red font-semibold" : "text-ios-muted"} disabled:opacity-40 hover:bg-ios-bg/60`}
      >
        +/−
      </button>
      <EditableNumberNullable value={value} currency={currency} onCommit={onCommit} signed />
    </div>
  );
}

// Editable-Select für Enum-Felder (z.B. usageType). Klick öffnet native <select>,
// `change` persistiert direkt — kein Save-Button, gleicher Pattern wie EditableText.
function EditableSelect<T extends string>({ value, options, onCommit, renderLabel }: {
  value: T;
  options: { value: T; label: string }[];
  onCommit: (v: T) => void;
  renderLabel?: (v: T) => React.ReactNode;
}) {
  const [editing, setEditing] = useState(false);
  const ref = useRef<HTMLSelectElement>(null);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);
  if (!editing) {
    const opt = options.find((o) => o.value === value);
    return (
      <button onClick={() => setEditing(true)} className="text-left -ml-1 px-1 py-0.5 rounded hover:bg-ios-bg/60">
        {renderLabel ? renderLabel(value) : opt?.label ?? value}
      </button>
    );
  }
  return (
    <select
      ref={ref}
      value={value}
      onChange={(e) => { const v = e.target.value as T; setEditing(false); if (v !== value) onCommit(v); }}
      onBlur={() => setEditing(false)}
      onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); setEditing(false); } }}
      className="-ml-1 px-1 py-0.5 rounded bg-white ring-1 ring-ios-blue outline-none text-[16px] lg:text-[14px]"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

// Datums-Inline-Edit (YYYY-MM-DD <input type="date">). Read-Format = lokal („07.03.2026"),
// Edit-Format = HTML-Spec ISO-Slice. Auf Commit nur das ISO-Datum (Tag-genau) durchreichen —
// HeaderPatch.receiptDate erwartet genau das.
function EditableDate({ value, onCommit }: { value: string | null; onCommit: (iso: string) => void }) {
  const [editing, setEditing] = useState(false);
  const iso = value ? new Date(value).toISOString().slice(0, 10) : "";
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);
  if (!editing) {
    return (
      <button onClick={() => setEditing(true)} className="text-left -ml-1 px-1 py-0.5 rounded hover:bg-ios-bg/60">
        {value ? formatDate(value) : <span className="text-ios-muted">—</span>}
      </button>
    );
  }
  return (
    <input
      ref={ref}
      type="date"
      defaultValue={iso}
      onBlur={(e) => {
        setEditing(false);
        const next = e.target.value;
        if (next && next !== iso) onCommit(next);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.stopPropagation(); (e.target as HTMLInputElement).blur(); }
        if (e.key === "Escape") { e.stopPropagation(); setEditing(false); }
      }}
      className="-ml-1 px-1 py-0.5 rounded bg-white ring-1 ring-ios-blue outline-none text-[16px] lg:text-[14px]"
    />
  );
}

// ── Tags-Editor ──────────────────────────────────────────────────────────────
function TagsEditor({ receiptId, tags }: { receiptId: string; tags: { id: string; name: string; colorHex: string | null }[] }) {
  const add = useAddTag(receiptId);
  const remove = useRemoveTag(receiptId);
  const { data: all } = useTags();
  const [input, setInput] = useState("");

  const submit = () => {
    const name = input.trim();
    if (!name) return;
    add.mutate(name, { onSuccess: () => setInput("") });
  };

  return (
    <div className="ios-card p-4">
      <h2 className="text-[15px] font-semibold mb-2">Tags</h2>
      <div className="flex flex-wrap gap-1.5 items-center">
        {tags.map((t) => (
          <span key={t.id} className="text-[12px] px-2 py-0.5 rounded-full bg-ios-line/60 text-ios-ink inline-flex items-center gap-1" style={t.colorHex ? { backgroundColor: `${t.colorHex}22`, color: t.colorHex } : undefined}>
            {t.name}
            <button onClick={() => remove.mutate(t.id)} className="hover:text-ios-red"><XMarkIcon className="w-3 h-3" /></button>
          </span>
        ))}
        <input
          list="tag-suggestions"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
          placeholder="+ Tag"
          className="text-[13px] px-2 py-0.5 rounded-full bg-ios-bg/60 outline-none focus:ring-1 focus:ring-ios-blue w-24"
        />
        <datalist id="tag-suggestions">
          {(all?.tags ?? []).map((t) => <option key={t.id} value={t.name} />)}
        </datalist>
      </div>
    </div>
  );
}

// ── Technische Meta-Daten (eigene Karte unter Tags) ─────────────────────────
// Erfassungs-Zeitstempel, Quelle, OCR-Status/Engine — sind Provenienz, nicht Inhalt.
// Vom HeaderCard getrennt, damit der Kopf nur die fachlichen Felder zeigt
// (User-Anforderung 2026-06-05).
function TechMetaCard({ receipt }: { receipt: NonNullable<ReturnType<typeof useReceipt>["data"]> }) {
  const r = receipt;
  return (
    <div className="ios-card p-4">
      <h2 className="text-[15px] font-semibold mb-2">Technische Daten</h2>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-[14px]">
        <Row label="Erfasst">{formatDateTime(r.captureDate)}</Row>
        <Row label="Quelle">{r.source}</Row>
        <Row label="OCR-Status">
          <span className={ocrStatusClass(r.ocrStatus)}>{r.ocrStatus}</span>
          {r.ocrConfidence && <span className="ml-2 text-ios-muted">({r.ocrConfidence})</span>}
        </Row>
        {r.ocrEngine && <Row label="OCR-Engine">{r.ocrEngine}</Row>}
      </dl>
    </div>
  );
}

// ── Audit-Drawer ─────────────────────────────────────────────────────────────
function AuditDrawer({ receiptId, jahr, open, onClose }: { receiptId: string; jahr: number | null; open: boolean; onClose: () => void }) {
  const { data } = useReceiptAudit(receiptId, open);
  // Buchungsart-Einträge speichern IDs (stabil bei Umbenennung) — angezeigt werden die Namen.
  const { data: artDaten } = useBuchungsarten(open ? jahr : null);
  const artName = (id: string | null | undefined) => (id ? artDaten?.arten.find((a) => a.id === id)?.name ?? "Buchungsart (nicht im Steuerjahr)" : "—");
  const zeige = (field: string, v: string | null) => {
    if (v === null) return "∅";
    if (field === "steuerArtId") return artName(v);
    if (field === "anteile") {
      try {
        return (JSON.parse(v) as { steuerArtId: string | null; anteilBp: number }[])
          .map((t) => `${String(t.anteilBp / 100).replace(".", ",")} % ${artName(t.steuerArtId)}`).join(" · ");
      } catch { return v; }
    }
    return v;
  };
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-40">
        <Transition.Child as={Fragment} enter="transition-opacity" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>
        <Transition.Child as={Fragment} enter="transition-transform" enterFrom="translate-x-full" enterTo="translate-x-0" leave="transition-transform" leaveFrom="translate-x-0" leaveTo="translate-x-full">
          <Dialog.Panel className="fixed right-0 top-0 h-full w-full max-w-md bg-ios-card shadow-xl flex flex-col">
            <div className="px-5 py-4 border-b border-ios-line/60 flex items-center justify-between">
              <Dialog.Title className="text-[16px] font-semibold">Änderungs-Verlauf</Dialog.Title>
              <button onClick={onClose} className="text-ios-muted hover:text-ios-ink"><XMarkIcon className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {!data ? (
                <p className="text-[14px] text-ios-muted">Lade…</p>
              ) : data.entries.length === 0 ? (
                <p className="text-[14px] text-ios-muted">Noch keine Korrekturen erfasst.</p>
              ) : (
                data.entries.map((e, i) => (
                  <div key={i} className="text-[13px] border-l-2 border-ios-blue/40 pl-3">
                    <div className="text-ios-muted">
                      {formatDateTime(e.correctedAt)} · {e.label} · <span className="font-medium text-ios-ink">{e.field === "steuerArtId" ? "Buchungsart" : e.field === "anteile" ? "Aufteilung" : e.field}</span>{e.user ? ` · ${e.user}` : ""}
                    </div>
                    <div className="mt-0.5">
                      <span className="text-ios-red line-through">{zeige(e.field, e.oldValue)}</span>
                      <span className="mx-1 text-ios-muted">→</span>
                      <span className="text-ios-blue">{zeige(e.field, e.newValue)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Dialog.Panel>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}

// ── Confirm-Modal (kein window.confirm) ──────────────────────────────────────
function ConfirmModal({ open, title, body, confirmLabel, pending, danger, onConfirm, onCancel }: {
  open: boolean; title: string; body: string; confirmLabel: string; pending?: boolean; danger?: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onCancel} className="relative z-50">
        <Transition.Child as={Fragment} enter="transition-opacity" enterFrom="opacity-0" enterTo="opacity-100" leave="transition-opacity" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child as={Fragment} enter="transition-transform" enterFrom="scale-95 opacity-0" enterTo="scale-100 opacity-100" leave="transition" leaveFrom="scale-100 opacity-100" leaveTo="scale-95 opacity-0">
            <Dialog.Panel className="ios-card p-5 max-w-sm w-full">
              <Dialog.Title className="text-[17px] font-semibold mb-1">{title}</Dialog.Title>
              <p className="text-[14px] text-ios-muted mb-4">{body}</p>
              <div className="flex justify-end gap-2">
                <button onClick={onCancel} className="text-[14px] px-3 py-1.5 rounded-ios bg-ios-line/60 hover:bg-ios-line">Abbrechen</button>
                <button onClick={onConfirm} disabled={pending} className={`text-[14px] px-3 py-1.5 rounded-ios text-white disabled:opacity-50 ${danger ? "bg-ios-red" : "bg-ios-blue"} hover:opacity-90`}>
                  {pending ? "…" : confirmLabel}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

function BackLink() {
  const navigate = useNavigate();
  const location = useLocation();
  // react-router v6: key === "default" ⇒ Direkteinstieg ohne In-App-History (Reload/Lesezeichen).
  // Dann gibt es kein sinnvolles "zurück" → Fallback aufs Archiv. Sonst retrace (führt zurück
  // zum Herkunfts-Monat im Archiv ODER zur Inbox — je nachdem, woher man kam).
  const canGoBack = location.key !== "default";
  return (
    <button
      type="button"
      onClick={() => (canGoBack ? navigate(-1) : navigate("/archiv"))}
      className="inline-flex items-center gap-1 text-[15px] text-ios-blue hover:text-ios-blueHover"
    >
      <ChevronLeftIcon className="w-4 h-4" />
      Zurück
    </button>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-ios-muted">{label}</dt>
      <dd className="text-ios-ink">{children}</dd>
    </>
  );
}

function ocrStatusClass(status: string): string {
  switch (status) {
    case "success":
      return "text-ios-green";
    case "failed":
      return "text-ios-red";
    case "pending":
    case "running":
      return "text-ios-orange";
    default:
      return "text-ios-muted";
  }
}

// ── Migration 0014: Zahlungsart-Select + Bezahldatum-Feld im Beleg-Header ─────
function PaymentMethodSelect({
  value,
  onCommit,
}: {
  value: PaymentMethod | null;
  onCommit: (v: PaymentMethod | null) => void;
}) {
  return (
    <select
      value={value ?? ""}
      onChange={(e) => onCommit((e.target.value as PaymentMethod) || null)}
      className="bg-ios-bg/60 text-[16px] lg:text-[14px] px-2 py-1 rounded-ios focus:ring-1 focus:ring-ios-blue outline-none max-w-full"
      aria-label="Zahlungsart"
    >
      <option value="">— nicht gesetzt —</option>
      {PAYMENT_METHODS_ORDER.map((m) => (
        <option key={m} value={m}>{PAYMENT_METHOD_LABEL[m] ?? m}</option>
      ))}
    </select>
  );
}

function PaymentDateField({
  receiptDate,
  paymentDate,
  onCommit,
}: {
  receiptDate: string | null; // ISO-Timestamp
  paymentDate: string | null; // ISO-Timestamp
  onCommit: (v: string | null) => void;
}) {
  // paymentDate NULL = „bezahlt am Belegdatum" — kein eigenes Datum nötig.
  // Wir zeigen das Belegdatum als Fallback an + Button „abweichend"; sobald Wert gesetzt,
  // wird daraus ein Datepicker + Differenz-Hinweis.
  const paymentYmd = paymentDate ? paymentDate.slice(0, 10) : null;
  const receiptYmd = receiptDate ? receiptDate.slice(0, 10) : null;
  const isDifferent = paymentYmd !== null && paymentYmd !== receiptYmd;

  if (paymentYmd === null) {
    return (
      <div className="inline-flex items-center gap-2">
        <span className="text-ios-muted text-[14px]">
          {receiptYmd ? formatDate(receiptDate) : "—"} <span className="text-[11px]">(= Belegdatum)</span>
        </span>
        <button
          type="button"
          onClick={() => onCommit(receiptYmd ?? new Date().toISOString().slice(0, 10))}
          className="text-[12px] text-ios-blue hover:text-ios-blueHover"
        >
          abweichend setzen
        </button>
      </div>
    );
  }

  const diffDays = (() => {
    if (!receiptYmd) return null;
    const a = Date.parse(`${receiptYmd}T00:00:00Z`);
    const b = Date.parse(`${paymentYmd}T00:00:00Z`);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return Math.round((b - a) / (24 * 3600_000));
  })();

  return (
    <div className="inline-flex items-center gap-2 flex-wrap">
      <input
        type="date"
        value={paymentYmd}
        onChange={(e) => onCommit(e.target.value || null)}
        className="bg-ios-bg/60 text-[16px] lg:text-[14px] px-2 py-1 rounded-ios focus:ring-1 focus:ring-ios-blue outline-none"
      />
      <button
        type="button"
        onClick={() => onCommit(null)}
        title="Bezahldatum zurücksetzen — gilt wieder als am Belegdatum bezahlt"
        className="text-[12px] text-ios-blue hover:text-ios-blueHover"
      >
        ↺
      </button>
      {isDifferent && diffDays !== null && (
        <span className="text-[12px] text-ios-orange">
          ⚠️ {diffDays > 0 ? `+${diffDays}` : diffDays} Tage ggü. Belegdatum
        </span>
      )}
    </div>
  );
}

// ── Oberflächen-Paket: Buchungsart des Belegs (ersetzt die Verwendungsart im Kopf) ─────────────────────────────
// „Privat“ oder „gemischt“ mit Beträgen je Art — gerechnet NUR über betragJeBuchungsart(effektiveBetraege), wie die Steuer-Rechnung.
function BuchungsartUebersicht({ receipt }: { receipt: NonNullable<ReturnType<typeof useReceipt>["data"]> }) {
  const { data } = useBuchungsarten(receipt.steuerjahr);
  const name = new Map<string, string>((data?.arten ?? []).map((a) => [a.id, a.name]));
  const items = [...receipt.lineItems].sort((a, b) => a.position - b.position);
  if (items.length === 0) return null;
  const eff = effektiveBetraege(items, receipt.totalDiscountGross);
  const je = betragJeBuchungsart(
    items.map((it, i) => ({ id: it.id, betrag: eff[i]!.eff, steuerArtId: it.steuerArtId })),
    new Map(items.map((it) => [it.id, it.anteile])),
  );
  const eintraege = [...je.entries()].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));
  const ohne = je.get(null);
  const mitArt = eintraege.filter(([k]) => k !== null);
  return (
    <span className="text-right" title="Summe je Buchungsart (effektive Beträge nach Rabatten)">
      {mitArt.length === 1 && ohne === undefined ? (
        <span>Buchungsart: <span className="text-ios-ink">{name.get(mitArt[0]![0]!) ?? "…"}</span></span>
      ) : (
        <span>
          {mitArt.length > 1 ? "gemischt: " : "Buchungsart: "}
          {mitArt.map(([k, v], i) => (
            <span key={k ?? "ohne"}>{i > 0 && " · "}{name.get(k!) ?? "…"} {formatCurrency(v, receipt.currency)}</span>
          ))}
          {ohne !== undefined && (
            <span className="text-ios-blue">{mitArt.length > 0 && " · "}⚠ ohne Buchungsart {formatCurrency(ohne, receipt.currency)}</span>
          )}
        </span>
      )}
    </span>
  );
}
